/**
 * Authentication context.
 *
 * Holds the current user + token bundle, persists tokens to localStorage,
 * and supplies an ``ApiClient`` instance wired to those tokens. The client
 * is shared with all child components via the `useApi` hook.
 *
 * Security note: refresh tokens are stored in localStorage for the v1 slice.
 * A subsequent PR moves them to an httpOnly+Secure cookie set by the backend,
 * which requires backend changes. localStorage is acceptable here because
 * (a) we have no third-party JS, (b) the access token is short-lived
 * (15 min), and (c) the refresh token is rotated on every use.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { ApiClient, type TokenStore } from "../api/client";
import { authApi, type AuthResponse, type UserPublic } from "../api/auth";
import { useLanguage } from "../lib/i18n";

const STORAGE_KEY = "vuzlyk.auth";

interface PersistedAuth {
  user: UserPublic;
  accessToken: string;
  refreshToken: string;
}

interface AuthContextValue {
  user: UserPublic | null;
  isAuthenticated: boolean;
  client: ApiClient;
  signup(credentials: { email: string; password: string }): Promise<void>;
  login(credentials: { email: string; password: string }): Promise<void>;
  logout(): Promise<void>;
  /**
   * Adopt an :class:`AuthResponse` minted by an alternate flow (e.g.
   * the Google OAuth exchange). Same persistence + state path as
   * :meth:`login`, just without making the call ourselves.
   */
  adopt(response: AuthResponse): void;
  /**
   * Merge ``patch`` into the persisted user. Used by settings pages
   * after a successful ``PATCH /auth/me/...`` so the in-memory copy
   * matches the server without a re-login.
   */
  updateUser(patch: Partial<UserPublic>): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  /** Override the storage layer for tests. */
  storage?: Storage;
  /** Override the fetch implementation for tests. */
  fetchImpl?: typeof fetch;
}

export function AuthProvider({ children, storage, fetchImpl }: AuthProviderProps): JSX.Element {
  const store = storage ?? globalThis.localStorage;
  const { setLang } = useLanguage();

  const [state, setState] = useState<PersistedAuth | null>(() => readPersisted(store));
  const stateRef = useRef(state);
  stateRef.current = state;

  // Server is the source of truth for the user's language pick; mirror
  // it into LanguageProvider whenever a session loads or the persisted
  // user changes. ``null`` means the user has never picked one — we
  // leave the in-memory value (localStorage seed or default) alone.
  useEffect(() => {
    const lang = state?.user.language;
    if (lang === "en" || lang === "uk") {
      setLang(lang);
    }
  }, [state?.user.language, setLang]);

  const tokenStore = useMemo<TokenStore>(
    () => ({
      getAccessToken: () => stateRef.current?.accessToken ?? null,
      getRefreshToken: () => stateRef.current?.refreshToken ?? null,
      setTokens: ({ accessToken, refreshToken }) => {
        const current = stateRef.current;
        if (!current) return;
        const next = { ...current, accessToken, refreshToken };
        writePersisted(store, next);
        setState(next);
      },
      clear: () => {
        store.removeItem(STORAGE_KEY);
        setState(null);
      },
    }),
    [store],
  );

  const onAuthFailure = useCallback(() => {
    store.removeItem(STORAGE_KEY);
    setState(null);
  }, [store]);

  const client = useMemo(() => {
    const opts: { tokens: TokenStore; onAuthFailure: () => void; fetchImpl?: typeof fetch } = {
      tokens: tokenStore,
      onAuthFailure,
    };
    if (fetchImpl !== undefined) {
      opts.fetchImpl = fetchImpl;
    }
    return new ApiClient(opts);
  }, [tokenStore, fetchImpl, onAuthFailure]);
  const api = useMemo(() => authApi(client), [client]);

  const adopt = useCallback(
    (response: AuthResponse) => {
      const next: PersistedAuth = {
        user: response.user,
        accessToken: response.tokens.access_token,
        refreshToken: response.tokens.refresh_token,
      };
      writePersisted(store, next);
      setState(next);
    },
    [store],
  );

  const updateUser = useCallback(
    (patch: Partial<UserPublic>) => {
      const current = stateRef.current;
      if (!current) return;
      const next: PersistedAuth = {
        ...current,
        user: { ...current.user, ...patch },
      };
      writePersisted(store, next);
      setState(next);
    },
    [store],
  );

  const signup = useCallback(
    async (credentials: { email: string; password: string }) => {
      const response = await api.signup(credentials);
      adopt(response);
    },
    [api, adopt],
  );

  const login = useCallback(
    async (credentials: { email: string; password: string }) => {
      const response = await api.login(credentials);
      adopt(response);
    },
    [api, adopt],
  );

  const logout = useCallback(async () => {
    const refresh = stateRef.current?.refreshToken;
    if (refresh) {
      try {
        await api.logout(refresh);
      } catch {
        // best-effort: even if logout endpoint fails, drop the local session
      }
    }
    store.removeItem(STORAGE_KEY);
    setState(null);
  }, [api, store]);

  // Re-sync if another tab updates auth state.
  useEffect(() => {
    if (!storage) {
      const handler = (event: StorageEvent): void => {
        if (event.key === STORAGE_KEY) {
          setState(readPersisted(globalThis.localStorage));
        }
      };
      window.addEventListener("storage", handler);
      return () => window.removeEventListener("storage", handler);
    }
    return undefined;
  }, [storage]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: state?.user ?? null,
      isAuthenticated: state !== null,
      client,
      signup,
      login,
      logout,
      adopt,
      updateUser,
    }),
    [state, client, signup, login, logout, adopt, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

export function useApi(): ApiClient {
  return useAuth().client;
}

function readPersisted(store: Storage): PersistedAuth | null {
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedAuth;
    if (
      typeof parsed?.accessToken === "string" &&
      typeof parsed?.refreshToken === "string" &&
      typeof parsed?.user?.id === "string"
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writePersisted(store: Storage, value: PersistedAuth): void {
  store.setItem(STORAGE_KEY, JSON.stringify(value));
}
